import React from 'react';

import {getMonth, getYear} from 'date-fns';
import i18n from 'i18next';
import {ReactDatePickerProps} from 'react-datepicker';
import {useTranslation} from 'react-i18next';

import 'react-datepicker/dist/react-datepicker.css';
import {ReactComponent as LeftArrowIcon} from '../../../assets/svg/arrow-left.svg';

import {I18N_NS} from '../../../core/constants/names';
import {yearsOptions} from '../../../utils/select';

import {Icon} from '../Icon';
import {Select, SelectOption} from '../Select';

import {DatePickerInput} from './input';
import {DatePickerArrow, DatePickerHead, InnerDatePicker} from './styled';

export type DatePickerProps = Omit<ReactDatePickerProps, 'onChange'> & {
    onChange?: (date: Date | null, event: React.SyntheticEvent<any> | undefined) => void;
    value?: Date | null;
    label?: string;
    hasError?: boolean;
    formFieldName?: string;
    isRequired?: boolean;
};

export const DatePicker = ({
    onChange = () => {},
    hasError,
    customInput,
    className,
    label,
    value,
    isRequired = false,
    ...other
}: DatePickerProps) => {
    const {t} = useTranslation(I18N_NS);
    const options = yearsOptions();
    const monthsKeys = [
        'january',
        'february',
        'march',
        'april',
        'may',
        'june',
        'july',
        'august',
        'september',
        'october',
        'november',
        'december',
    ];
    const months = monthsKeys.map((key, i) => ({
        label: t('dictionaries.months.' + key),
        value: i,
    }));

    const renderCustomHeader = ({
        date,
        decreaseMonth,
        increaseMonth,
        changeMonth,
        changeYear,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled,
    }: Parameters<NonNullable<ReactDatePickerProps['renderCustomHeader']>>[0]) => (
        <DatePickerHead>
            <DatePickerArrow
                type="button"
                onClick={decreaseMonth}
                disabled={prevMonthButtonDisabled}
            >
                <Icon source={LeftArrowIcon} height={14} width={14} />
            </DatePickerArrow>
            <Select
                options={months}
                value={months.find((o) => o.value === getMonth(date))}
                onChange={(v: SelectOption<number>) => v.value && changeMonth(v.value)}
            />
            <Select
                options={options}
                value={options.find((o) => o.value === getYear(date))}
                onChange={(v: SelectOption<number>) => v.value && changeYear(v.value)}
            />
            <DatePickerArrow
                type="button"
                onClick={increaseMonth}
                disabled={nextMonthButtonDisabled}
                right
            >
                <Icon source={LeftArrowIcon} height={14} width={14} />
            </DatePickerArrow>
        </DatePickerHead>
    );

    return (
        <InnerDatePicker
            selected={value}
            renderCustomHeader={renderCustomHeader}
            onChange={onChange}
            popperClassName={className}
            showPopperArrow={false}
            dateFormat="dd.MM.yyyy"
            autoComplete="off"
            locale={i18n.language}
            customInput={
                React.isValidElement(customInput) ? (
                    <customInput.type {...customInput.props} label={label} hasError={hasError} />
                ) : (
                    <DatePickerInput
                        isRequired={isRequired}
                        label={label}
                        hasError={hasError}
                        placeholder={t('components.datepicker.SelectDate')}
                    />
                )
            }
            {...other}
        />
    );
};

export {globalStyles as globalDatePickerStyles} from './styled';
